import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useOutlet } from "@/lib/outlet";
import { toast } from "sonner";
import {
  AlertOctagon, PackageX, FileWarning, UserCheck, ShoppingBag, ReceiptText,
} from "lucide-react";

/**
 * Exception Queue — the daily work list surfaced by control hardening v1.
 * Live counts for the six control-related exception classes plus deep-lists
 * for each. All rows are filterable by the active outlet.
 */
export default function ExceptionQueue() {
  const { outletParam, outlet } = useOutlet();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openKind, setOpenKind] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(
        "/staff/procurement/reports/exception-queue",
        { params: outletParam ? { outlet: outletParam } : {} },
      );
      setData(d);
    } catch { toast.error("Could not load exception queue"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [outletParam]);

  const CARDS = [
    { key: "unpriced_po_lines",  title: "Unpriced PO lines",   icon: FileWarning,  hint: "CTRL-01 · rate = 0 with qty > 0" },
    { key: "unpriced_grn_lines", title: "Unpriced GRN lines",  icon: PackageX,     hint: "CTRL-01 · would block approval" },
    { key: "missing_invoice_no", title: "Missing invoice #",   icon: ReceiptText,  hint: "CTRL-03 · approved without invoice" },
    { key: "self_approved_grns", title: "Self-approved > ₹2k", icon: UserCheck,    hint: "CTRL-04 · creator = approver" },
    { key: "direct_grns",        title: "Direct (walk-in) GRNs", icon: ShoppingBag, hint: "no PO ancestor" },
  ];

  if (loading) return <div className="p-8 text-sm text-[hsl(var(--ink-soft))]">Loading exception queue…</div>;
  if (!data) return null;

  return (
    <div data-testid="exception-queue">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="eyebrow">Daily work list</div>
          <h2 className="mt-1 display text-2xl">
            Exception Queue{" "}
            <span className="text-sm text-[hsl(var(--ink-soft))] font-normal">· {outlet === "ALL" ? "All outlets" : outlet}</span>
          </h2>
        </div>
        <button data-testid="exq-refresh" onClick={load}
                className="text-xs uppercase tracking-wider text-[hsl(var(--brass-2))] hover:underline">
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        {CARDS.map((c) => {
          const Icon = c.icon;
          const count = data.counts?.[c.key] || 0;
          const active = openKind === c.key;
          return (
            <button
              key={c.key}
              data-testid={`exq-card-${c.key}`}
              onClick={() => setOpenKind(active ? null : c.key)}
              className={`text-left p-4 border transition-colors ${
                active
                  ? "border-[hsl(var(--forest))] bg-[hsl(var(--bg-2))]"
                  : count > 0
                  ? "border-[hsl(6_62%_45%_/_0.4)] bg-[hsl(6_60%_98%)]"
                  : "border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                  {c.title}
                </div>
                <Icon size={18} className={count > 0 ? "text-[hsl(6_62%_45%)]/70" : "text-[hsl(var(--ink-soft))]/40"} />
              </div>
              <div className={`mt-1 display text-3xl ${count > 0 ? "text-[hsl(6_62%_45%)]" : "text-[hsl(var(--ink-soft))]"}`}>{count}</div>
              <div className="mt-1 text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]/80">{c.hint}</div>
            </button>
          );
        })}
      </div>

      {openKind && (data[openKind] || []).length > 0 && (
        <div className="mt-6 border border-[hsl(var(--rule))]">
          <div className="p-3 bg-[hsl(var(--bg-2))] text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] flex items-center gap-2">
            <AlertOctagon size={14} className="text-[hsl(6_62%_45%)]" />
            {CARDS.find((c) => c.key === openKind)?.title} · {(data[openKind] || []).length} rows
          </div>
          <table className="w-full text-xs">
            <ExceptionRows kind={openKind} rows={data[openKind]} />
          </table>
        </div>
      )}
      {openKind && (data[openKind] || []).length === 0 && (
        <div className="mt-6 p-6 border border-dashed border-[hsl(var(--rule))] text-sm text-[hsl(var(--ink-soft))]">
          Nothing in this queue right now.
        </div>
      )}
    </div>
  );
}

function ExceptionRows({ kind, rows }) {
  if (kind === "unpriced_po_lines") return (
    <tbody className="divide-y divide-[hsl(var(--rule))]">
      <TableHead cols={["PO Number", "Outlet", "Line", "Item", "Qty"]} />
      {rows.map((r, i) => (
        <tr key={i} data-testid={`exq-row-${kind}-${i}`}>
          <Td mono>{r.po_number}</Td><Td>{r.outlet || "—"}</Td>
          <Td>{r.line_no}</Td><Td>{r.item}</Td><Td right>{r.qty}</Td>
        </tr>
      ))}
    </tbody>
  );
  if (kind === "unpriced_grn_lines") return (
    <tbody className="divide-y divide-[hsl(var(--rule))]">
      <TableHead cols={["GRN Number", "Status", "Outlet", "Line", "Item", "Qty"]} />
      {rows.map((r, i) => (
        <tr key={i} data-testid={`exq-row-${kind}-${i}`}>
          <Td mono>{r.grn_number}</Td><Td>{r.status}</Td><Td>{r.outlet || "—"}</Td>
          <Td>{r.line_no}</Td><Td>{r.item}</Td><Td right>{r.qty}</Td>
        </tr>
      ))}
    </tbody>
  );
  if (kind === "missing_invoice_no") return (
    <tbody className="divide-y divide-[hsl(var(--rule))]">
      <TableHead cols={["GRN Number", "Vendor", "Outlet", "Total"]} />
      {rows.map((r, i) => (
        <tr key={i} data-testid={`exq-row-${kind}-${i}`}>
          <Td mono>{r.grn_number}</Td><Td>{r.vendor_name}</Td><Td>{r.outlet || "—"}</Td>
          <Td right>₹{Number(r.total).toLocaleString("en-IN")}</Td>
        </tr>
      ))}
    </tbody>
  );
  if (kind === "self_approved_grns") return (
    <tbody className="divide-y divide-[hsl(var(--rule))]">
      <TableHead cols={["GRN Number", "PO Number", "User", "Total", "Outlet"]} />
      {rows.map((r, i) => (
        <tr key={i} data-testid={`exq-row-${kind}-${i}`}>
          <Td mono>{r.grn_number}</Td><Td mono>{r.po_number || "—"}</Td>
          <Td>{r.user}</Td>
          <Td right>₹{Number(r.total).toLocaleString("en-IN")}</Td>
          <Td>{r.outlet || "—"}</Td>
        </tr>
      ))}
    </tbody>
  );
  if (kind === "direct_grns") return (
    <tbody className="divide-y divide-[hsl(var(--rule))]">
      <TableHead cols={["GRN Number", "Vendor", "Total", "Outlet"]} />
      {rows.map((r, i) => (
        <tr key={i} data-testid={`exq-row-${kind}-${i}`}>
          <Td mono>{r.grn_number}</Td><Td>{r.vendor_name}</Td>
          <Td right>₹{Number(r.total).toLocaleString("en-IN")}</Td>
          <Td>{r.outlet || "—"}</Td>
        </tr>
      ))}
    </tbody>
  );
  return null;
}

function TableHead({ cols }) {
  return (
    <thead className="bg-[hsl(var(--bg-2))]">
      <tr>{cols.map((c) => <th key={c} className="text-left p-2 text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">{c}</th>)}</tr>
    </thead>
  );
}

function Td({ children, right, mono }) {
  return (
    <td className={`p-2 ${right ? "text-right" : ""} ${mono ? "font-mono text-[hsl(var(--brass-2))]" : ""}`}>
      {children}
    </td>
  );
}
